import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileText, Plus, Trash2, Timer } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { calculateLaborCost, calculateTotalCost } from './utils';

/** MaintWorkNotesDialog — 4-column cost summary (hours, rate, labor,
 *  total-with-materials) + timeline of notes. */
export const MaintWorkNotesDialog = ({
  open,
  onOpenChange,
  task,
  newWorkNote,
  setNewWorkNote,
  newWorkHours,
  setNewWorkHours,
  onAddNote,
  onDeleteNote,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="sm:max-w-[550px] max-h-[80vh] overflow-y-auto"
      data-testid="maint-work-notes-dialog"
    >
      <DialogHeader>
        <DialogTitle className="font-manrope flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Work Notes &amp; Hours - {task?.title}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg">
          <div>
            <p className="text-xs text-slate-500 uppercase">Hours</p>
            <p className="text-lg font-bold text-slate-900">{task?.hours_worked || 0}h</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Rate</p>
            <p className="text-lg font-bold text-slate-900">${task?.hourly_rate || 0}/hr</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Labor</p>
            <p className="text-lg font-bold text-blue-600">
              ${calculateLaborCost(task || {}).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Total Cost</p>
            <p className="text-lg font-bold text-emerald-600">
              ${calculateTotalCost(task || {}).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Add Work Note */}
        <div className="space-y-2 p-3 border border-slate-200 rounded-lg">
          <Label>Add Work Note</Label>
          <Textarea
            value={newWorkNote}
            onChange={(e) => setNewWorkNote(e.target.value)}
            placeholder="Describe work completed..."
            rows={2}
            data-testid="maint-work-note-input"
          />
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Hours Worked</Label>
              <Input
                type="number"
                min="0"
                step="0.25"
                value={newWorkHours}
                onChange={(e) => setNewWorkHours(e.target.value)}
                placeholder="0.0"
                data-testid="maint-work-hours-input"
              />
            </div>
            <Button
              onClick={onAddNote}
              disabled={!newWorkNote.trim()}
              data-testid="maint-add-work-note-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Note
            </Button>
          </div>
        </div>

        {/* Work Notes List */}
        <div className="space-y-2">
          <Label>Work History</Label>
          {task?.work_notes && task.work_notes.length > 0 ? (
            <div className="space-y-2">
              {task.work_notes.map((note, idx) => (
                <div
                  key={note.id || idx}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg group"
                >
                  <div className="flex-1">
                    <p className="text-sm text-slate-800">{note.note}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      {note.hours > 0 && (
                        <span className="flex items-center gap-1">
                          <Timer className="h-3 w-3" />
                          {note.hours}h
                        </span>
                      )}
                      {note.timestamp && (
                        <span>{format(parseISO(note.timestamp), 'MMM d, h:mm a')}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onDeleteNote(note.id)}
                    className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                    data-testid={`maint-delete-note-${idx}`}
                    aria-label="Delete work note"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-4">
              No work notes yet. Add notes to track progress and hours.
            </p>
          )}
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default MaintWorkNotesDialog;
